import { useState, useMemo } from "react";
import { Calculator, TrendingUp, Info } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

const fmtEUR = (n) => Math.round(n).toLocaleString("de-DE") + " €";

/**
 * Simplified AfA calculator for new-build condominiums (§7 Abs. 5a EStG).
 *
 *   - 5 % degressive AfA on the building share for the first 6 years
 *     (years 1 through 5 use degressive method; assumed effective ~5 % p.a.
 *     here, simplified for marketing purposes – actual application depends
 *     on individual tax situation).
 *   - The building share is typically 80 % of the purchase price (the rest
 *     is the land share, which is not depreciable).
 *   - Tax savings = AfA × marginal tax rate.
 */
export const InvestorAfaCalculator = () => {
    const t = useT();
    const [price, setPrice] = useState(723000);
    const [taxRate, setTaxRate] = useState(42); // %
    const [buildingShare, setBuildingShare] = useState(80); // %

    const result = useMemo(() => {
        const buildingValue = price * (buildingShare / 100);
        const yearlyAfa = buildingValue * 0.05; // simplified 5 % p.a.
        const yearlySavings = yearlyAfa * (taxRate / 100);
        const totalAfa5y = yearlyAfa * 5;
        const totalSavings5y = yearlySavings * 5;
        return { buildingValue, yearlyAfa, yearlySavings, totalAfa5y, totalSavings5y };
    }, [price, buildingShare, taxRate]);

    return (
        <section
            id="afa-rechner"
            className="bg-white py-24 sm:py-32"
            data-testid="afa-calculator-section"
        >
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-10 mb-12">
                    <div className="lg:col-span-5">
                        <span className="overline">
                            {t({ de: "AfA-Rechner · Beispielrechnung", en: "AfA calculator · example" })}
                        </span>
                        <h2 className="h-section mt-4 text-balance">
                            {t({ de: "5 % AfA für", en: "5 % depreciation for" })}
                            <span className="italic text-brand-500"> {t({ de: "Neubau nutzen.", en: "new builds." })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="body-md">
                            {t({
                                de: "Nach §7 Abs. 5a EStG dürfen Sie für Wohnungs-Neubau (Effizienzhaus 40, Baubeginn ab 1. Oktober 2023) in den ersten Jahren degressiv mit 5 % p.a. abschreiben. Hier ein vereinfachtes Beispiel – die endgültige Berechnung hängt von Ihrer individuellen Steuersituation ab.",
                                en: "Under §7 (5a) German Income Tax Act, new-build apartments (energy class 40, construction start from 1 Oct 2023) can be depreciated at 5 % p.a. (declining balance) in the first years. The final calculation depends on your individual tax situation.",
                            })}
                        </p>
                    </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden">
                    {/* Inputs */}
                    <div className="bg-white p-8 lg:p-10 space-y-7" data-testid="afa-inputs">
                        <SliderField
                            label={t({ de: "Kaufpreis", en: "Purchase price" })}
                            value={price}
                            min={400000}
                            max={1000000}
                            step={1000}
                            onChange={setPrice}
                            unit={fmtEUR(price)}
                            testid="afa-price"
                        />
                        <SliderField
                            label={t({ de: "Gebäudeanteil", en: "Building share" })}
                            value={buildingShare}
                            min={70}
                            max={90}
                            step={1}
                            onChange={setBuildingShare}
                            unit={`${buildingShare} %`}
                            testid="afa-building-share"
                            helper={t({
                                de: "Üblich 75–85 %, Rest ist Grundanteil (nicht abschreibbar).",
                                en: "Typically 75–85 %, the remainder is the land share (not depreciable).",
                            })}
                        />
                        <SliderField
                            label={t({ de: "Persönlicher Steuersatz", en: "Personal tax rate" })}
                            value={taxRate}
                            min={20}
                            max={45}
                            step={1}
                            onChange={setTaxRate}
                            unit={`${taxRate} %`}
                            testid="afa-tax-rate"
                            helper={t({
                                de: "Inklusive Soli (Spitzensteuersatz aktuell ~42 %).",
                                en: "Including solidarity surcharge (top rate currently ~42 %).",
                            })}
                        />
                    </div>

                    {/* Results */}
                    <div className="bg-ink text-white p-8 lg:p-10 flex flex-col justify-between" data-testid="afa-results">
                        <div>
                            <div className="flex items-center gap-2 mb-6">
                                <Calculator size={18} className="text-clay-300" />
                                <span className="overline text-clay-300">
                                    {t({ de: "Ihre Beispielrechnung", en: "Your example" })}
                                </span>
                            </div>

                            <div className="space-y-4">
                                <ResultRow
                                    label={t({ de: "Abschreibungs-Bemessungsgrundlage (Gebäude)", en: "AfA basis (building share)" })}
                                    value={fmtEUR(result.buildingValue)}
                                    muted
                                />
                                <ResultRow
                                    label={t({ de: "Jährliche AfA (5 %)", en: "Annual depreciation (5 %)" })}
                                    value={fmtEUR(result.yearlyAfa)}
                                />
                                <ResultRow
                                    label={t({ de: "Steuerersparnis pro Jahr", en: "Tax savings per year" })}
                                    value={fmtEUR(result.yearlySavings)}
                                />
                            </div>

                            <div className="mt-10 pt-6 border-t border-white/15">
                                <div className="flex items-center gap-2 mb-2">
                                    <TrendingUp size={14} className="text-clay-300" />
                                    <span className="overline text-clay-300">
                                        {t({ de: "5 Jahre kumuliert", en: "Cumulated 5 years" })}
                                    </span>
                                </div>
                                <div className="font-heading text-5xl text-clay-300 mt-2" data-testid="afa-total-savings">
                                    {fmtEUR(result.totalSavings5y)}
                                </div>
                                <div className="font-body text-sm text-white/70 mt-1">
                                    {t({ de: "Steuerersparnis über 5 Jahre", en: "tax savings over 5 years" })}
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 flex items-start gap-3 p-4 bg-clay-300/10 border border-clay-300/30">
                            <Info size={16} className="text-clay-300 shrink-0 mt-0.5" />
                            <p className="font-body text-xs text-white/80 leading-relaxed">
                                {t({
                                    de: "Diese Beispielrechnung dient nur der Orientierung. Sie ersetzt keine Steuerberatung. Wir empfehlen, die individuelle Konstellation mit Ihrem Steuerberater zu besprechen.",
                                    en: "This example is for orientation only and is not tax advice. We recommend discussing your individual situation with your tax adviser.",
                                })}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const SliderField = ({ label, value, min, max, step, onChange, unit, testid, helper }) => (
    <div>
        <div className="flex items-baseline justify-between mb-2">
            <label className="font-body text-sm font-medium text-stone-700">{label}</label>
            <span className="font-heading text-base text-brand-600">{unit}</span>
        </div>
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            className="w-full accent-brand-500"
            data-testid={testid}
        />
        {helper && (
            <p className="font-body text-xs text-stone-500 mt-1.5 leading-relaxed">{helper}</p>
        )}
    </div>
);

const ResultRow = ({ label, value, muted }) => (
    <div className="flex items-baseline justify-between">
        <span className={`font-body text-sm ${muted ? "text-white/60" : "text-white/80"}`}>{label}</span>
        <span className={`font-heading ${muted ? "text-base text-white/80" : "text-lg text-white"}`}>
            {value}
        </span>
    </div>
);
